import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';
import { ArrowLeft, Search, ChevronRight } from 'lucide-react';

const LibertadoresPage = () => {
  const navigate = useNavigate();
  const [selectedPhase, setSelectedPhase] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const altitudeFactors = [
    {
      city: 'La Paz',
      altitude: 3600,
      team: 'Bolívar',
      homeRecord: 89,
      tips: 'Times brasileiros perdem 65% da capacidade aeróbica',
      country: 'Bolivia'
    },
    {
      city: 'Quito',
      altitude: 2850,
      team: 'LDU/Barcelona',
      homeRecord: 76,
      tips: 'Bola voa 15% mais longe, gols de longe aumentam',
      country: 'Ecuador'
    },
    {
      city: 'Bogotá',
      altitude: 2640,
      team: 'Millonarios/Santa Fe',
      homeRecord: 71,
      tips: 'Pressão psicológica + altitude = pesadelo visitante',
      country: 'Colombia'
    },
    {
      city: 'Cochabamba',
      altitude: 2550,
      team: 'Jorge Wilstermann',
      homeRecord: 68,
      tips: 'Under 2.5 gols tem 78% de acerto',
      country: 'Bolivia'
    },
    {
      city: 'Medellín',
      altitude: 1495,
      team: 'Atlético Nacional',
      homeRecord: 62,
      tips: 'Altitude leve, mas calor afeta visitantes',
      country: 'Colombia'
    },
    {
      city: 'México City',
      altitude: 2250,
      team: 'Club América',
      homeRecord: 65,
      tips: 'Poluição + altitude = cansaço rápido',
      country: 'México'
    }
  ];

  const groupStageSecrets = [
    {
      title: 'Fase de Grupos: Terra de Ninguém',
      phase: 'groups',
      zebraRate: 42,
      description: 'Primeiras 3 rodadas são imprevisíveis. Times se estudam, zebras históricas acontecem.',
      strategy: 'Aposte em dupla chance para azarões casa'
    },
    {
      title: 'Rodadas 4-6: Definição',
      phase: 'groups',
      zebraRate: 23,
      description: 'Times já se conhecem. Favoritos assumem controle, mas pressão por classificação gera surpresas.',
      strategy: 'Value bets em times desesperados'
    },
    {
      title: 'Oitavas: Cautela Total',
      phase: 'knockout',
      zebraRate: 31,
      description: 'Jogos de ida sempre truncados. Ninguém quer sair em desvantagem.',
      strategy: 'Ida: Under gols. Volta: Over e drama'
    },
    {
      title: 'Quartas/Semis: Emoção Pura',
      phase: 'knockout',
      zebraRate: 28,
      description: 'Drama máximo nos últimos 30 minutos. Times assumem riscos.',
      strategy: 'Over 2.5 na volta, pênaltis possíveis'
    },
    {
      title: 'Final: Tensão Máxima',
      phase: 'final',
      zebraRate: 35,
      description: 'Jogo único, tensão paralisa. Poucos riscos, muita estratégia.',
      strategy: 'Under 2.5 e empate 90min'
    }
  ];

  const continentalDynamics = [
    {
      country: 'Brasil',
      strengths: 'Investimento, elenco, técnicos europeus',
      weaknesses: 'Viagens longas, altitude, calendário',
      winRate: 34,
      style: 'Ofensivo e físico',
      bestTeams: 'Flamengo, Palmeiras, Atlético-MG',
      titles: 22
    },
    {
      country: 'Argentina',
      strengths: 'Tradição, paixão, experiência continental',
      weaknesses: 'Crise econômica, êxodo de talentos',
      winRate: 28,
      style: 'Tático e guerreiro',
      bestTeams: 'River Plate, Boca Juniors, Racing',
      titles: 25
    },
    {
      country: 'Uruguai',
      strengths: 'Garra, união, nunca desistem',
      weaknesses: 'Falta investimento, mercado pequeno',
      winRate: 12,
      style: 'Defensivo e pegajoso',
      bestTeams: 'Nacional, Peñarol',
      titles: 8
    },
    {
      country: 'Colômbia',
      strengths: 'Altitude, torcida, talento individual',
      weaknesses: 'Instabilidade, violência, poucos recursos',
      winRate: 8,
      style: 'Técnico e criativo',
      bestTeams: 'Atlético Nacional, Millonarios',
      titles: 3
    }
  ];

  const faqs = [
    {
      question: 'Por que a altitude é tão decisiva na Libertadores?',
      answer: 'Acima de 2.500m, o ar tem 25% menos oxigênio. Atletas de elite perdem até 30% da capacidade aeróbica, afetando velocidade, precisão e tomada de decisão. Times como Bolívar (La Paz, 3600m) têm 89% de aproveitamento em casa justamente por isso.'
    },
    {
      question: 'Qual é a diferença entre apostar na fase de grupos vs mata-mata?',
      answer: 'Fase de grupos tem 42% de zebras (times se estudando), ideal para dupla chance em azarões. Mata-mata: jogos de ida são travados (73% Under 2.5), jogos de volta explodem (68% Over 2.5). São competições completamente diferentes.'
    },
    {
      question: 'Como a IA identifica oportunidades em times brasileiros fora do Brasil?',
      answer: 'Analisamos 23 variáveis geográficas: distância viajada, altitude, clima, fuso horário, etc. Times brasileiros perdem 32% do rendimento em viagens 3000km+. Nossa IA detecta quando essas variáveis estão sendo subestimadas pelas odds.'
    },
    {
      question: 'Rivalidades como Boca vs River realmente afetam as apostas?',
      answer: 'Absolutamente! Superclásicos têm padrões únicos: 85% Under 1.5 no 1º tempo, média de 8.3 cartões/jogo, 67% vão para prorrogação. Nossa IA tem modelos específicos para cada grande rivalidade continental.'
    }
  ];

  const PHASE_LABELS = {
    all: 'Todas Fases',
    groups: 'Fase de Grupos',
    knockout: 'Mata-Mata',
    final: 'Final'
  };

  const filteredSecrets = groupStageSecrets.filter(secret => {
    const matchesPhase = selectedPhase === 'all' || secret.phase === selectedPhase;
    const matchesSearch = secret.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          secret.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesPhase && matchesSearch;
  });

  const phaseCount = groupStageSecrets.reduce((acc, secret) => {
    acc[secret.phase] = (acc[secret.phase] || 0) + 1;
    return acc;
  }, {});

  // Structured Data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Copa Libertadores 2024: Guia Completo com IA | Palpites e Estratégias",
    "description": "Guia definitivo da Copa Libertadores 2024 com análise de IA. Descubra segredos da altitude, dinâmica continental, mata-mata e estratégias de apostas certeiras.",
    "author": {
      "@type": "Organization",
      "name": "PickStarAI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStarAI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
    "datePublished": "2025-08-22",
    "dateModified": new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/libertadores"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="Copa Libertadores 2024: Guia Completo com IA | Palpites e Estratégias"
        description="Guia definitivo da Copa Libertadores 2024 com análise de IA. Descubra segredos da altitude, dinâmica continental, mata-mata e estratégias de apostas certeiras."
        keywords="copa libertadores 2024, palpites libertadores, apostas conmebol, análise ia futebol, libertadores estratégias, altitude futebol, mata-mata apostas"
        canonical="https://pickstarai.com/libertadores"
        ogImage="https://pickstarai.com/og-libertadores.jpg"
        structuredData={[articleStructuredData, faqStructuredData]}
      />

      <div className="min-h-screen bg-black">
        {/* Glass layer background */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />

        {/* Terminal-style Header */}
        <header className="fixed top-0 z-50 w-full bg-black border-b border-white/[0.05]">
          <div className="max-w-screen-xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="flex items-center text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-[0.15em]"
              >
                <ArrowLeft className="w-3 h-3 mr-2" />
                VOLTAR
              </Link>
              <div className="text-[9px] text-white/30 uppercase tracking-[0.2em]">
                ATUALIZADO: {new Date().toLocaleDateString('pt-BR').toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="min-h-[60vh] flex items-center justify-center px-8 pt-20 pb-12">
          <div className="text-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <h1 className="text-5xl lg:text-7xl font-thin text-white/95 leading-none mb-2">
                Copa Libertadores
              </h1>
              <p className="text-xs text-white/50 uppercase tracking-[0.2em] mt-4">
                A competição mais apaixonante: altitude, rivalidades e mata-mata
              </p>
            </motion.div>

            {/* Search Bar */}
            <div className="mt-8 max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/30 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar fases ou estratégias..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/[0.02] border border-white/[0.05] rounded text-sm text-white/80 placeholder-white/30 focus:outline-none focus:border-white/10 transition-all"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 py-12">

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mb-16 pb-12 border-b border-white/[0.05]">
            <div className="text-center">
              <div className="text-3xl font-mono text-white/95">134</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">VARIÁVEIS</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-emerald-400/80">89%</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">LA PAZ CASA</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-white/95">42%</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">ZEBRAS</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-emerald-400/80">PRO</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">PRECISÃO</div>
            </div>
          </div>

          {/* Phase Filter */}
          <div className="mb-12">
            <div className="flex gap-2 overflow-x-auto">
              {Object.entries(PHASE_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSelectedPhase(key)}
                  className={`px-4 py-2 text-xs font-medium uppercase tracking-wider transition-all whitespace-nowrap ${
                    selectedPhase === key
                      ? 'bg-emerald-500/20 text-emerald-400/80 border border-emerald-500/30'
                      : 'bg-white/[0.02] text-white/50 border border-white/[0.05] hover:border-white/10'
                  }`}
                >
                  {label} {key !== 'all' && `(${phaseCount[key] || 0})`}
                </button>
              ))}
            </div>
          </div>

          {/* Altitude Factors */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">GEOGRAFIA</span>
              ALTITUDE: O FATOR MAIS DECISIVO
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {altitudeFactors.map((factor, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-white/90">{factor.city}</h3>
                      <p className="text-xs text-white/50">{factor.team}</p>
                      <p className="text-[10px] text-white/40 uppercase tracking-wider">{factor.country}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-mono text-emerald-400/80">{factor.altitude}m</div>
                    </div>
                  </div>

                  <div className="mb-3 pb-3 border-b border-white/[0.05]">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/40 text-xs uppercase tracking-wider">Casa:</span>
                      <span className="font-mono text-emerald-400/80">{factor.homeRecord}%</span>
                    </div>
                  </div>

                  <div>
                    <span className="text-[10px] text-white/40 uppercase tracking-wider">Dica: </span>
                    <span className="text-xs text-white/60">{factor.tips}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Group Stage Secrets */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">ESTRATÉGIA</span>
              DINÂMICA DAS FASES
            </h2>

            <div className="space-y-4">
              {filteredSecrets.map((secret, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-white/80 uppercase tracking-[0.15em] mb-2">
                        {secret.title}
                      </h3>
                      <p className="text-sm text-white/50">{secret.description}</p>
                    </div>
                    <div className="text-center ml-4">
                      <div className="text-2xl font-mono text-emerald-400/80">{secret.zebraRate}%</div>
                      <div className="text-[10px] text-white/30 uppercase tracking-wider">ZEBRAS</div>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-white/[0.05]">
                    <span className="text-[10px] text-white/40 uppercase tracking-wider">Estratégia: </span>
                    <span className="text-sm text-white/60">{secret.strategy}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Continental Dynamics */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">PAÍSES</span>
              DINÂMICA CONTINENTAL
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {continentalDynamics.map((country, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-white/90">{country.country}</h3>
                    <div className="text-center">
                      <div className="text-lg font-mono text-emerald-400/80">{country.titles}</div>
                      <div className="text-[10px] text-white/30 uppercase tracking-wider">TÍTULOS</div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm mb-3 pb-3 border-b border-white/[0.05]">
                    <div>
                      <span className="text-emerald-400/80 text-xs uppercase tracking-wider">Forças: </span>
                      <span className="text-white/60">{country.strengths}</span>
                    </div>
                    <div>
                      <span className="text-white/40 text-xs uppercase tracking-wider">Fraquezas: </span>
                      <span className="text-white/50">{country.weaknesses}</span>
                    </div>
                    <div>
                      <span className="text-white/40 text-xs uppercase tracking-wider">Estilo: </span>
                      <span className="text-white/60">{country.style}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/40 text-xs uppercase tracking-wider">Taxa de vitória:</span>
                    <span className="font-mono text-white/80">{country.winRate}%</span>
                  </div>
                  <div className="mt-2">
                    <span className="text-[10px] text-white/40 uppercase tracking-wider">Destaques: </span>
                    <span className="text-xs text-white/60">{country.bestTeams}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">FAQ</span>
              PERGUNTAS FREQUENTES
            </h2>

            <div className="space-y-0 border border-white/[0.05]">
              {faqs.map((faq, index) => (
                <details key={index} className="group">
                  <summary className="px-6 py-4 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05] last:border-b-0">
                    <span className="text-sm text-white/80 font-medium">{faq.question}</span>
                    <ChevronRight className="w-4 h-4 text-white/40 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-6 py-4 text-sm text-white/60 leading-relaxed border-b border-white/[0.05] last:border-b-0">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center py-12 border-t border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-4">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-2">COMEÇAR</span>
              CONQUISTE A GLÓRIA CONTINENTAL
            </h2>
            <p className="text-sm text-white/50 mb-8 max-w-2xl mx-auto">
              Junte-se a mais de 25.000 apostadores que já dominam os segredos da Copa Libertadores com nossa tecnologia exclusiva.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                       text-sm font-medium text-white uppercase tracking-[0.15em]
                       hover:from-emerald-500 hover:to-violet-500 transition-all"
            >
              ANÁLISE GRATUITA DA LIBERTADORES
              <ChevronRight className="ml-2 w-4 h-4" />
            </button>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-white/[0.05]">
          <SEOFooter language="pt-BR" />
        </div>
      </div>
    </>
  );
};

export default LibertadoresPage;
